import React, { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { routePaths } from 'config/routes';
import { useModal } from 'context/modal';
import { useTabInfo, useFeesUpdate } from 'hooks/query/useJob';
import { useDefault } from 'hooks/query/useCommon';
import { ModalButton } from 'components/newComponents/Buttons';
import Modal from 'components/newComponents/Modal';
import FeeItem from './FeeItem';

const FeesModal = () => {
  const { modalNames, close, info } = useModal();
  const { pathname } = useLocation();

  const {
    params: { id, tab },
  } = matchPath(pathname, { path: routePaths.JOBS });

  const {
    data: { fees: defaultItems },
  } = useDefault();

  const {
    data: { job_fees: jobFees },
  } = useTabInfo(id, tab);

  const { mutate: updateFees } = useFeesUpdate(id, tab);

  const isVisible = info(modalNames.fees).visible;

  const closeHandler = useCallback(() => close(modalNames.fees), [close, modalNames.fees]);

  const fees = useMemo(() => {
    return jobFees.reduce((acc, e) => {
      return { ...acc, [e.fee_id]: e };
    }, {});
  }, [jobFees]);

  const defaultFees = useMemo(() => {
    return defaultItems.reduce((acc, e) => {
      return { ...acc, [e.id]: { ...e, checked: !!fees[e.id], amount: fees[e.id] ? fees[e.id].amount : e.default_amount } };
    }, {});
  }, [defaultItems, fees]);

  const updateFeesHandler = useCallback(
    ({ items }) => {
      const prepared = Object.values(items)
        .filter((e) => e.checked)
        .map((e) => ({ job_id: id, fee_id: e.id, amount: e.amount }));

      updateFees(
        {
          job_id: id,
          job_fees: prepared,
        },
        { onSuccess: closeHandler }
      );
    },
    [updateFees, id, closeHandler]
  );

  const { values, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      items: defaultFees,
    },
    onSubmit: updateFeesHandler,
  });

  const onSelectHandler = useCallback(
    (id) => {
      const { [id]: current, ...rest } = values.items;
      const isChecked = !current.checked;

      setFieldValue('items', {
        ...rest,
        [id]: { ...current, checked: isChecked, amount: isChecked ? current.default_amount : 0 },
      });
    },
    [setFieldValue, values.items]
  );

  const onChangeHandler = useCallback(
    (id, amount) => {
      const { [id]: current, ...rest } = values.items;

      setFieldValue('items', { ...rest, [id]: { ...current, amount } });
    },
    [setFieldValue, values.items]
  );

  return (
    <Modal style={{ width: 600 }} header="Fees" show={isVisible} onHide={closeHandler}>
      <form onSubmit={handleSubmit}>
        <div className="new-table modal">
          <div className="new-table--row">
            <div className="new-table--row--item">
              <h4 className="subHeader p-0">Name</h4>
            </div>
            <div className="new-table--row--item">
              <h4 className="subHeader p-0">Amount</h4>
            </div>
          </div>

          {Object.values(values.items).map((item) => (
            <FeeItem key={item.id} item={item} onSelect={onSelectHandler} handleChange={onChangeHandler} />
          ))}
        </div>
        <div className="footer-buttons">
          <ModalButton title="Cancel" variant="cancel" onClick={closeHandler} />
          <ModalButton title="Save" variant="confirm" type="submit" />
        </div>
      </form>
    </Modal>
  );
};

export default FeesModal;
